/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React from 'react';
import { Icon } from './ComponentIcon';
import { AppTooltip } from './AppTooltip';
import { TableQuickSearch } from './TableToolsSurface';
import { matchesAppsGridSearch } from './apps-grid-search.utils';
import './AppsGrid.css';

/** Grid'de kullanılacak uygulama tipi (diğer projelerde aynı alanlar ile kullanılabilir) */
export interface AppsGridAppInfo {
  code: string;
  name: string;
  description?: string;
  /** Seçili dilde kısa kurumsal tooltip açıklaması. */
  tooltipDescription?: string;
  icon: string;
  /** `GET /api/apps` tam SVG (Core API kataloğu); grid öncelikli `iconSvg` kullanır. */
  iconSvg?: string;
  colorLight: string;
  colorDark: string;
  path: string;
  displayOrder?: number;
  /** Çok dilli ad (locale → string); API apps.registry label. */
  label?: Record<string, string>;
  /** Çok dilli açıklama (locale → string); API apps.registry descriptionLabel. */
  descriptionLabel?: Record<string, string>;
  /** Çok dilli kısa tooltip açıklaması; Core apps registry tek kaynaktır. */
  tooltipDescriptionLabel?: Record<string, string>;
  /** Modül için ana uygulama kodu; appsOrder üst kod (ör. ERP) ile modül kodu eşlemesi. */
  orderParentCode?: string;
}

export interface AppsGridProps {
  /** Uygulama listesi */
  apps: AppsGridAppInfo[];
  /** Görünen ad: (app) => string. Verilmezse app.name kullanılır. */
  getLabel?: (app: AppsGridAppInfo) => string;
  /** Kısa kurumsal tooltip açıklaması; seçili locale ile çözülmüş değer verilmelidir. */
  getTooltipDescription?: (app: AppsGridAppInfo) => string;
  /** @deprecated Geriye uyumluluk. Yeni kullanımlarda getTooltipDescription kullanın. */
  getTitle?: (app: AppsGridAppInfo) => string;
  /** Link adresi. Verilmezse span veya button. Mevcut uygulamada da href varsa link (is-current + aria-current). */
  getHref?: (app: AppsGridAppInfo) => string | undefined;
  /** Tema: 'dark' | diğer. İkon rengi için. */
  theme?: string;
  /** İkon rengi. Verilmezse theme'e göre colorLight/colorDark kullanılır. */
  getIconColor?: (app: Pick<AppsGridAppInfo, 'code' | 'colorLight' | 'colorDark'>, theme: string) => string;
  /** Şu anki uygulama kodu (küçük harf). Eşleşen öğe is-current + aria-current alır; getHref varsa yine link (aynı uygulamaya git / kök yenile). */
  currentAppCode?: string;
  /** Öğe tıklanınca (link kullanılmıyorsa veya ek işlem için). */
  onItemClick?: (app: AppsGridAppInfo) => void;
  /** Grid sütun sayısı. */
  columns?: number;
  /** İkon boyutu (px). */
  iconSize?: number;
  /** Grid wrapper sınıfı. */
  className?: string;
  /** Öğe wrapper sınıfı. */
  itemClassName?: string;
  /** role (menü vb.). */
  role?: 'menu' | 'list' | 'navigation' | 'presentation';
  /** Düzenleme/Sıralama özelliği aktif edilsin mi? (Kendi içinde Edit icon, titreşim ve order mekanizmasını çalıştırır) */
  isManageable?: boolean;
  /** Dışarıdan uzak (API) sırayı çekerken kullanılacak handler */
  fetchRemoteOrder?: () => Promise<string[] | undefined>;
  /** İlk render için önceden bilinen kullanıcı sırası */
  initialOrder?: string[];
  /** Sıralama değişip kayıt altına alınması gerektiğinde çağrılır */
  onSyncOrder?: (codes: string[]) => void;
  /** Özel ikon; verilmezse önce `iconSvg`, yoksa `Icon name={icon}` (uyumluluk). */
  renderIcon?: (app: AppsGridAppInfo, size: number, color: string) => React.ReactNode;
  /** Edit-bar sol tarafına başlık (ör. "Uygulamalar"). */
  headerTitle?: string;
  /** Opt-in compact search; the host supplies labels in the active language. */
  search?: { label: string; placeholder?: string; emptyLabel: string };
  editLabel?: string;
  cancelLabel?: string;
  doneLabel?: string;
  /** Dropdown altına "Tüm Uygulamalar" gibi bir footer link ekler. Verilmezse gösterilmez. */
  footerHref?: string;
  footerLabel?: string;
  footerOnClick?: () => void;
}

const defaultGetIconColor = (
  app: Pick<AppsGridAppInfo, 'code' | 'colorLight' | 'colorDark'>,
  theme: string,
): string => (theme === 'dark' ? app.colorDark : app.colorLight);

/**
 * API SVG innerHTML normalize:
 * 1) XML bildirimi kaldırılır.
 * 2) Sabit renkler `currentColor`'a çevrilir — Core'a hangi logo eklenirse eklensin
 *    açık/koyu modda tema rengine uyar (tek kaynak Core; uydularda elle düzeltme gerekmez).
 *    `none` (outline şekiller) ve zaten `currentColor` olanlar korunur.
 */
function normalizeApiIconSvg(html: string): string {
  return html
    .replace(/^\s*<\?xml[^?]*\?>\s*/i, '')
    // fill="#fff" / stroke='rgb(...)' → currentColor (none ve currentColor hariç)
    .replace(/(fill|stroke)\s*=\s*"(?!\s*(?:none|currentColor)\s*")[^"]*"/gi, '$1="currentColor"')
    .replace(/(fill|stroke)\s*=\s*'(?!\s*(?:none|currentColor)\s*')[^']*'/gi, "$1='currentColor'")
    // style="fill:#fff" / stroke:#000 → currentColor (none/currentColor hariç)
    .replace(/(fill|stroke)\s*:\s*(?!\s*(?:none|currentColor)\s*[;"'])[^;"']+/gi, '$1:currentColor')
    .trim();
}

function DefaultAppGridIcon(props: { app: AppsGridAppInfo; iconSize: number }): React.ReactElement {
  const { app, iconSize } = props;
  const raw = app.iconSvg?.trim();
  if (raw) {
    return (
      <span
        className="apps-grid-api-icon"
        style={{ '--apps-grid-api-icon-size': `${iconSize}px` } as React.CSSProperties}
        dangerouslySetInnerHTML={{ __html: normalizeApiIconSvg(raw) }}
        aria-hidden
      />
    );
  }
  return <Icon name={app.icon} size={iconSize} />;
}

/** Sıra anahtarı: son segment (module code), büyük harf — appsOrder ile registry büyük/küçük harf farkını giderir */
function normAppCodeKey(code: string | undefined): string {
  return String(code ?? '')
    .split('_')
    .pop()
    ?.trim()
    .toUpperCase() || '';
}

/** appsOrder dizisindeki en iyi indeks: önce satır kodu, yoksa üst uygulama kodu (ör. ERP alt modülü). */
function orderIndexForApp(app: AppsGridAppInfo, normalizedOrder: string[]): number {
  const keys = [normAppCodeKey(app.code)];
  if (app.orderParentCode) {
    keys.push(normAppCodeKey(app.orderParentCode));
  }
  let best = -1;
  for (const k of keys) {
    if (!k) continue;
    const idx = normalizedOrder.indexOf(k);
    if (idx !== -1 && (best === -1 || idx < best)) {
      best = idx;
    }
  }
  return best;
}

function tieBreakDisplayOrder(a: AppsGridAppInfo, b: AppsGridAppInfo): number {
  const ao = Number(a.displayOrder);
  const bo = Number(b.displayOrder);
  const aok = Number.isFinite(ao) ? ao : Number.MAX_SAFE_INTEGER;
  const bok = Number.isFinite(bo) ? bo : Number.MAX_SAFE_INTEGER;
  if (aok !== bok) return aok - bok;
  return String(a.code || '').localeCompare(String(b.code || ''), undefined, { sensitivity: 'base' });
}

export function AppsGrid({
  apps,
  getLabel = (app) => app.name,
  getTooltipDescription,
  getTitle,
  getHref,
  theme = 'light',
  getIconColor = defaultGetIconColor,
  currentAppCode = '',
  onItemClick,
  columns = 3,
  iconSize = 24,
  className = '',
  itemClassName = '',
  role = 'menu',
  renderIcon,
  isManageable = false,
  fetchRemoteOrder,
  initialOrder,
  onSyncOrder,
  headerTitle,
  search,
  editLabel = 'Sıralamayı Düzenle',
  cancelLabel = 'İptal',
  doneLabel = 'Bitti',
  footerHref,
  footerLabel,
  footerOnClick,
}: AppsGridProps) {
  const applyOrderToApps = React.useCallback((sourceApps: AppsGridAppInfo[], orderArr: string[]): AppsGridAppInfo[] => {
    const normalizedOrder = orderArr.map((c) => normAppCodeKey(String(c)));
    return [...sourceApps].sort((a, b) => {
      const idxA = orderIndexForApp(a, normalizedOrder);
      const idxB = orderIndexForApp(b, normalizedOrder);
      if (idxA === -1 && idxB === -1) return tieBreakDisplayOrder(a, b);
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      if (idxA !== idxB) return idxA - idxB;
      return tieBreakDisplayOrder(a, b);
    });
  }, []);

  const [localApps, setLocalApps] = React.useState(() => {
    if (
      isManageable &&
      Array.isArray(initialOrder) &&
      initialOrder.length > 0 &&
      apps.length > 0
    ) {
      return applyOrderToApps(apps, initialOrder);
    }
    return apps;
  });
  const [isEditing, setIsEditing] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [preEditApps, setPreEditApps] = React.useState<AppsGridAppInfo[]>([]);
  const visibleApps = React.useMemo(() => (
    search && !isEditing
      ? localApps.filter((app) => matchesAppsGridSearch(searchTerm, app.code, getLabel(app)))
      : localApps
  ), [localApps, search, isEditing, searchTerm, getLabel]);

  React.useEffect(() => {
    if (!isManageable) {
      setLocalApps(apps);
      return;
    }

    if (Array.isArray(initialOrder) && initialOrder.length > 0) {
      if (apps.length === 0) {
        return;
      }
      setLocalApps(applyOrderToApps(apps, initialOrder));
      return;
    }

    // Sıralama yalnızca API'den okunur — localStorage kullanılmaz
    const runRemoteCheck = async () => {
      if (fetchRemoteOrder) {
        try {
          const remoteOrder = await fetchRemoteOrder();
          if (Array.isArray(remoteOrder) && remoteOrder.length > 0) {
            setLocalApps(applyOrderToApps(apps, remoteOrder));
            return;
          }
        } catch { /* ignore */ }
      }
      setLocalApps(apps);
    };

    runRemoteCheck();
  }, [apps, isManageable, fetchRemoteOrder, initialOrder, applyOrderToApps]);

  const [draggedItemIdx, setDraggedItemIdx] = React.useState<number | null>(null);
  const [dropTargetIdx, setDropTargetIdx] = React.useState<number | null>(null);
  const [hasOrderChanged, setHasOrderChanged] = React.useState(false);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (!isEditing) return;
    setDraggedItemIdx(index);
    setDropTargetIdx(index);
    setHasOrderChanged(false);
    e.dataTransfer.effectAllowed = 'move';

    const dragSource = e.currentTarget as HTMLElement;
    const iconElement = dragSource.querySelector('.apps-grid-icon') as HTMLElement | null;
    if (iconElement) {
      const dragImage = iconElement.cloneNode(true) as HTMLElement;
      dragImage.style.position = 'fixed';
      dragImage.style.top = '-1000px';
      dragImage.style.left = '-1000px';
      dragImage.style.pointerEvents = 'none';
      dragImage.style.margin = '0';
      dragImage.style.transform = 'none';
      dragImage.style.opacity = '0.95';
      document.body.appendChild(dragImage);
      e.dataTransfer.setDragImage(dragImage, dragImage.offsetWidth / 2, dragImage.offsetHeight / 2);
      requestAnimationFrame(() => {
        document.body.removeChild(dragImage);
      });
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (!isEditing || draggedItemIdx === null) return;
    if (dropTargetIdx !== index) {
      setDropTargetIdx(index);
    }
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (!isEditing || draggedItemIdx === null || draggedItemIdx === index) return;

    const newApps = [...localApps];
    const [item] = newApps.splice(draggedItemIdx, 1);
    if (!item) return;
    // Sol çizgi "hedef öğeden önce ekle" anlamına gelir.
    // Aşağı doğru taşımada, çıkarma sonrası hedef index 1 azalır.
    const insertIndex = draggedItemIdx < index ? index - 1 : index;
    newApps.splice(insertIndex, 0, item);
    setLocalApps(newApps);
    setHasOrderChanged(true);
  };

  const handleDragEnd = () => {
    setDraggedItemIdx(null);
    setDropTargetIdx(null);
    setHasOrderChanged(true);
  };

  const handleEditStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSearchTerm('');
    setPreEditApps([...localApps]);
    setHasOrderChanged(false);
    setIsEditing(true);
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalApps(preEditApps);
    setIsEditing(false);
  };

  const handleDoneEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);

    if (!hasOrderChanged && JSON.stringify(localApps) === JSON.stringify(preEditApps)) return;

    const codes = localApps.map((a) => normAppCodeKey(a.code));
    if (onSyncOrder) onSyncOrder(codes);
  };

  return (
    <div className="apps-grid-container" style={{ position: 'relative' }}>
      {(isManageable || search) && (
        <div className="apps-grid-edit-bar">
          {headerTitle && <span className="apps-grid-header-title">{headerTitle}</span>}
          <div className="apps-grid-header-actions">
            {search && !isEditing && (
              <TableQuickSearch
                value={searchTerm}
                onChange={setSearchTerm}
                label={search.label}
                placeholder={search.placeholder}
                maxLength={120}
                className="datatable-toolbar-search datatable-toolbar-search--compact apps-grid-search"
              />
            )}
            {isManageable && (!isEditing ? (
              <button
                type="button"
                className="apps-grid-edit-pill apps-grid-edit-pill--icon-only"
                aria-label={editLabel}
                title={editLabel}
                onClick={handleEditStart}
              >
                <Icon name="edit" size={16} />
              </button>
            ) : (
              <div className="apps-grid-edit-actions">
                <button
                  type="button"
                  className="apps-grid-edit-pill apps-grid-edit-pill--cancel"
                  onClick={handleCancelEdit}
                  title={cancelLabel}
                >
                  <Icon name="close" size={16} />
                  <span>{cancelLabel}</span>
                </button>
                <button
                  type="button"
                  className="apps-grid-edit-pill is-active"
                  onClick={handleDoneEdit}
                  title={doneLabel}
                >
                  <Icon name="check" size={16} />
                  <span>{doneLabel}</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div
        className={`apps-grid ${className} ${isEditing ? 'is-editing' : ''}`.trim()}
        style={{ '--apps-grid-columns': columns } as React.CSSProperties }
        role={role}
      >
      {visibleApps.map((app, index) => {
        const label = getLabel(app);
        const tooltipDescription = getTooltipDescription?.(app)
          ?? getTitle?.(app)
          ?? app.tooltipDescription
          ?? app.description
          ?? label;
        const href = getHref?.(app);
        const isCurrent = currentAppCode === app.code.toLowerCase();
        const iconColor = getIconColor(app, theme);

        const iconContent = renderIcon
          ? renderIcon(app, iconSize, iconColor)
          : <DefaultAppGridIcon app={app} iconSize={iconSize} />;

        const content = (
          <>
            <div
              className="apps-grid-icon"
              style={{ '--apps-grid-app-color': iconColor } as React.CSSProperties}
            >
              {iconContent}
            </div>
            <span className="apps-grid-name">{label}</span>
          </>
        );

        const withTooltip = (element: React.ReactElement<React.HTMLAttributes<HTMLElement>>) => (
          <AppTooltip
            key={app.code}
            label={label}
            description={tooltipDescription}
            disabled={isEditing}
          >
            {element}
          </AppTooltip>
        );

        if (isEditing) {
          return withTooltip(
            <div
              className={`apps-grid-item is-editable ${draggedItemIdx === index ? 'is-dragging' : ''} ${dropTargetIdx === index && draggedItemIdx !== null && draggedItemIdx !== index ? 'is-drop-target' : ''} ${itemClassName}`.trim()}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              {content}
            </div>
          );
        }

        if (isCurrent && href) {
          return withTooltip(
            <a
              href={href}
              className={`apps-grid-item is-current ${itemClassName}`.trim()}
              role={role === 'menu' ? 'menuitem' : undefined}
              aria-current="page"
              onClick={() => onItemClick?.(app)}
            >
              {content}
            </a>
          );
        }

        if (isCurrent) {
          return withTooltip(
            <span
              className={`apps-grid-item is-current ${itemClassName}`.trim()}
              role={role === 'menu' ? 'menuitem' : undefined}
            >
              {content}
            </span>
          );
        }

        if (href) {
          return withTooltip(
            <a
              href={href}
              className={`apps-grid-item ${itemClassName}`.trim()}
              role={role === 'menu' ? 'menuitem' : undefined}
              onClick={() => onItemClick?.(app)}
            >
              {content}
            </a>
          );
        }

        return withTooltip(
          <button
            type="button"
            className={`apps-grid-item ${itemClassName}`.trim()}
            role={role === 'menu' ? 'menuitem' : undefined}
            onClick={() => onItemClick?.(app)}
          >
            {content}
          </button>
        );
      })}
      </div>
      {search && searchTerm.trim() && visibleApps.length === 0 && (
        <div className="apps-grid-search-empty" role="status">{search.emptyLabel}</div>
      )}
      {footerHref && footerLabel && (
        <div className="apps-grid-footer">
          <a
            href={footerHref}
            className="apps-grid-footer-link"
            onClick={() => { if (footerOnClick) footerOnClick(); }}
          >
            <span>{footerLabel}</span>
          </a>
        </div>
      )}
    </div>
  );
}
